import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Input, DatePicker, Select, Button, Table, Statistic, Divider, Space, Tag, message, Form, ConfigProvider, theme } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ShoppingCartOutlined, FileTextOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';
import { MODULE_IDS, GST_RATES } from '../../utils/constants';
import { formatCurrency, formatQty, formatDate } from '../../utils/formatters';

import { getPurchases, getPurchaseById, createPurchase, updatePurchase, deletePurchase } from '../../api/rawPurchase.api';

import SearchableSelect from '../../components/common/SearchableSelect';
import ConfirmDelete from '../../components/common/ConfirmDelete';
import DataTable from '../../components/common/DataTable';

/* ─────────────────────────────────────────────
   Light Theme & Compact Override Styles
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-purchase-page {
      --bg: #f8fafc;
      --surface: #ffffff;
      --surface-2: #f1f5f9;
      --border: #e2e8f0;
      --border-hi: #cbd5e1;
      --text-1: #0f172a;
      --text-2: #334155;
      --text-3: #64748b;
      --accent: #f59e0b;
      font-family: 'Barlow', sans-serif;
    }

    .light-purchase-page .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 6px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
    }
    
    .compact-section {
      background: #f8fafc;
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 8px 12px;
      margin-bottom: 12px;
    }

    .section-title {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--text-3);
      margin-bottom: 6px;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .light-purchase-page .ant-form-item { margin-bottom: 0 !important; }
    .light-purchase-page .ant-form-item-label { padding-bottom: 2px; }
    .light-purchase-page .ant-form-item-label > label { font-size: 11px !important; color: var(--text-2); height: 18px; font-weight: 600; }
    
    .light-purchase-page .ant-input-sm, 
    .light-purchase-page .ant-select-sm .ant-select-selector,
    .light-purchase-page .ant-picker-sm {
      border-radius: 3px;
      font-size: 12px;
    }

    .light-purchase-page .ant-table-thead > tr > th {
      background: var(--surface-2) !important;
      color: var(--text-2) !important;
      font-weight: 600;
      font-size: 10px;
      text-transform: uppercase;
      padding: 4px 8px !important;
    }

    .light-purchase-page .ant-table-tbody > tr > td {
      padding: 4px 8px !important;
      font-size: 12px;
      color: var(--text-1);
    }

    .light-purchase-page .ant-statistic-title { font-size: 10px; margin-bottom: 2px; }
    .light-purchase-page .ant-statistic-content { font-size: 16px; }
  `}</style>
);

const S = {
  tableWrap: { border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden', marginBottom: 16 },
  emptyState: { textAlign: 'center', padding: '30px 20px', background: '#f8fafc', borderRadius: '6px', border: '1px dashed #cbd5e1' },
  emptyText: { color: '#64748b', fontSize: 13, marginTop: 8 },
  saveBtn: { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, padding: '0 24px', height: 40 }
};

// --- Sub-component for viewing expanded bill details ---
const ExpandedBillDetails = ({ plantId, rpsid }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['purchaseDetail', plantId, rpsid],
    queryFn: () => getPurchaseById(plantId, rpsid),
    enabled: !!rpsid && !!plantId,
  });

  const columns = [
    { title: 'Material', dataIndex: 'rawName', key: 'rawName' },
    { title: 'Qty', dataIndex: 'qty', key: 'qty', render: val => formatQty(val) },
    { title: 'Unit', dataIndex: 'unitDisplayName', key: 'unitDisplayName' },
    { title: 'Rate', dataIndex: 'rate', key: 'rate', render: val => formatCurrency(val) },
    { title: 'Dis.', dataIndex: 'discount', key: 'discount', render: val => formatCurrency(val) },
    { title: 'Taxable', dataIndex: 'taxable', key: 'taxable', render: val => formatCurrency(val) },
    { title: 'GST Amt', dataIndex: 'gstAmt', key: 'gstAmt', render: val => formatCurrency(val) },
    { title: 'Total', dataIndex: 'amount', key: 'amount', render: val => <span style={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(val)}</span> },
  ];

  return <Table columns={columns} dataSource={data?.details || []} pagination={false} size="small" loading={isLoading} rowKey="rpid" style={{ margin: '8px', border: '1px solid #e2e8f0' }} />;
};

// --- MAIN COMPONENT ---
const RawPurchasePage = () => {
  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();
  const { canAdd, canEdit, canDelete } = usePermissions(MODULE_IDS.RAW_PURCHASE);

  const [headerForm] = Form.useForm();
  const [itemForm] = Form.useForm();
  const rawMaterialRef = useRef(null);
  const containerRef = useRef(null); // Ref for global keyboard events

  const [lineItems, setLineItems] = useState([]);
  const [editingRpsid, setEditingRpsid] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedRaw, setSelectedRaw] = useState(null);
  
  const [historyParams, setHistoryParams] = useState({ current: 1, pageSize: 10, from: null, to: null, vid: null });
  const [historyVendorId, setHistoryVendorId] = useState(null);

  const currentGstPercent = Form.useWatch('gstPercent', headerForm) ?? 18;

  useEffect(() => {
    if (lineItems.length > 0) {
      setLineItems(prevLines => prevLines.map(item => {
        const effectiveRate = item.rate - (item.discount || 0);
        const taxable = item.qty * effectiveRate;
        const gstAmt = taxable * (currentGstPercent / 100);
        return { ...item, gstPercent: currentGstPercent, taxable, gstAmt, amount: taxable + gstAmt };
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGstPercent]);

  /* ─────────────────────────────────────────────
     Global Keydown Handler for Ant Design
  ───────────────────────────────────────────── */
  const handleGlobalKeyDown = (e) => {
    if (e.key !== 'Enter') return;

    // 1. Escape Hatch: If on Material Search and it's empty
    const isMaterialSearch = e.target.closest('.material-search-container');
    if (isMaterialSearch) {
        const inputVal = e.target.value;
        if (!inputVal && !selectedRaw) {
            e.preventDefault();
            document.querySelector('.btn-save-bill')?.focus();
            return;
        }
        return; 
    }

    // 2. Find all focusable inputs and buttons in the container
   


if (!containerRef.current) return;
    const focusable = containerRef.current.querySelectorAll(
      'input:not([disabled]):not([readonly]):not([type="hidden"]), ' +
      '.ant-select-selection-search-input:not([disabled]), ' + // Vendor aur GST ke liye
      '.ant-picker-input input:not([disabled]), ' +            // 🔴 NAYA: DatePicker ke liye
      'button:not([disabled]):not(.ant-btn-icon-only)' 
    );
    
    if (!focusable.length) return;
    const elementsArr = Array.from(focusable);
    const activeIndex = elementsArr.indexOf(document.activeElement);

    // 3. If 'Add' button is active
    if (document.activeElement.classList.contains('btn-add-item')) {
        e.preventDefault();
        handleAddLineItem();
        return;
    }

    // 4. If 'Save Bill' button is active
    if (document.activeElement.classList.contains('btn-save-bill')) {
        return; // Allow form submit
    }

    // 5. Jump to next field
    e.preventDefault();
    const nextIndex = activeIndex + 1;
    if (nextIndex < elementsArr.length) {
        const nextEl = elementsArr[nextIndex];
        nextEl.focus();
        if (nextEl.tagName === 'INPUT') {
            nextEl.select(); 
        }
    }
  };

  useEffect(() => {
    const handler = (e) => {
        if (containerRef.current && containerRef.current.contains(e.target)) {
            handleGlobalKeyDown(e);
        }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [selectedRaw]);

  /* Queries & Mutations */
  const { data: historyData, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['purchases', plantId, historyParams, historyVendorId],
    queryFn: () => getPurchases(plantId, {
      page: historyParams.current, pageSize: historyParams.pageSize,
      from: historyParams.from ? historyParams.from.format('YYYY-MM-DD') : null,
      to: historyParams.to ? historyParams.to.format('YYYY-MM-DD') : null,
      vid: historyVendorId
    }),
    enabled: !!plantId && !!historyVendorId,
  });

  const createMutation = useMutation({
    mutationFn: (payload) => createPurchase(plantId, payload),
    onSuccess: () => { message.success('Bill saved!'); handleResetForm(); queryClient.invalidateQueries(['purchases']); },
    onError: (err) => message.error(err.message || 'Error saving bill')
  });

  const updateMutation = useMutation({
    mutationFn: (payload) => updatePurchase(plantId, editingRpsid, payload),
    onSuccess: () => { message.success('Bill updated!'); handleResetForm(); queryClient.invalidateQueries(['purchases']); },
    onError: (err) => message.error(err.message || 'Error updating bill')
  });

  const deleteMutation = useMutation({
    mutationFn: (rpsid) => deletePurchase(plantId, rpsid),
    onSuccess: () => { message.success('Bill deleted!'); queryClient.invalidateQueries(['purchases']); },
    onError: (err) => message.error(err.message || 'Error deleting bill')
  });

  /* Handlers */
  const handleAddLineItem = async () => {
    try {
      const values = await itemForm.validateFields();
      if (!selectedRaw) return message.error("Please select a Raw Material");

      const qty = Number(values.qty);
      const rate = Number(values.rate);
      const discount = Number(values.discount || 0);

      const effectiveRate = rate - discount;
      const taxable = qty * effectiveRate;
      const gstAmt = taxable * (currentGstPercent / 100);
      const amount = taxable + gstAmt;

      const newItem = {
        id: Date.now(), rawID: selectedRaw.rawID, rawName: selectedRaw.rawName,
        hsnCode: values.hsnCode, qty, unitID: selectedRaw.unitID, unitDisplayName: selectedRaw.unitDisplayName,
        rate, discount, gstPercent: currentGstPercent, taxable, gstAmt, amount
      };

      setLineItems([...lineItems, newItem]);
      itemForm.resetFields();
      setSelectedRaw(null);

      // Loop Back Focus to Material Search
      setTimeout(() => {
        const input = document.querySelector('.material-search-container input');
        if (input) input.focus();
      }, 100);

    } catch (error) {
      // Validation failed silently
    }
  };
  // Page load hote hi Vendor field par auto-focus karne ke liye
  useEffect(() => {
    setTimeout(() => {
      const firstInput = document.querySelector('.vendor-search-container input');
      if (firstInput) {
        firstInput.focus();
      }
    }, 400); // Thoda delay taaki Ant Design render ho jaye
  }, []);

  const handleRemoveItem = (id) => setLineItems(lineItems.filter(item => item.id !== id));

  const handleSaveBill = async () => {
    try {
      const headerValues = await headerForm.validateFields();
      if (lineItems.length === 0) return message.error("Please add at least one item");

      const totals = calculateTotals();
      const payload = {
        billNo: headerValues.billNo, billDate: headerValues.billDate.format('YYYY-MM-DD'), vid: headerValues.vid,
        gstPercent: currentGstPercent, taxable: totals.taxable, gstAmt: totals.gst, totalAmount: totals.grand,
        details: lineItems.map(item => ({
          rawID: item.rawID, qty: item.qty, unitID: item.unitID, rate: item.rate,
          discount: item.discount, gstPercent: item.gstPercent, hsnCode: item.hsnCode
        }))
      };

      if (editingRpsid) updateMutation.mutate(payload); else createMutation.mutate(payload);
    } catch (error) { message.error("Fill required header fields"); }
  };

  const handleEditBill = async (record) => {
    try {
      const fullBill = await getPurchaseById(plantId, record.rpsid);
      setEditingRpsid(fullBill.rpsid);
      
      headerForm.setFieldsValue({ vid: fullBill.vid, billNo: fullBill.billNo, billDate: dayjs(fullBill.billDate), gstPercent: fullBill.gstPercent });
      setSelectedVendor({ vendorName: fullBill.vendorName, gstin: fullBill.gstin }); 

      const mappedItems = fullBill.details.map((d, index) => ({
        id: d.rpid || index, rawID: d.rawID, rawName: d.rawName, hsnCode: d.hsnCode,
        qty: d.qty, unitID: d.unitID, unitDisplayName: d.unitDisplayName, rate: d.rate,
        discount: d.discount, gstPercent: d.gstPercent, taxable: d.taxable, gstAmt: d.gstAmt, amount: d.amount
      }));
      setLineItems(mappedItems);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) { message.error("Failed to fetch bill details"); }
  };

  const handleResetForm = () => {
    headerForm.resetFields(); itemForm.resetFields(); setLineItems([]); setEditingRpsid(null); setSelectedVendor(null); setSelectedRaw(null);
  };

  const calculateTotals = () => lineItems.reduce((acc, curr) => ({
    taxable: acc.taxable + curr.taxable, gst: acc.gst + curr.gstAmt, grand: acc.grand + curr.amount
  }), { taxable: 0, gst: 0, grand: 0 });

  const totals = calculateTotals();

  const lineItemColumns = [
    { title: '#', render: (t, r, i) => i + 1, width: 30 },
    { title: 'Material', dataIndex: 'rawName', key: 'rawName' },
    { title: 'HSN', dataIndex: 'hsnCode', key: 'hsnCode', render: val => <span style={{ color: 'var(--text-3)' }}>{val || '-'}</span> },
    { title: 'Qty', dataIndex: 'qty', key: 'qty', render: val => formatQty(val) },
    { title: 'Unit', dataIndex: 'unitDisplayName', key: 'unitDisplayName', render: val => <Tag bordered={false} style={{ margin: 0 }}>{val}</Tag> },
    { title: 'Rate', dataIndex: 'rate', key: 'rate', render: val => formatCurrency(val) },
    { title: 'Disc.', dataIndex: 'discount', key: 'discount', render: val => <span style={{ color: 'var(--text-3)' }}>{formatCurrency(val)}</span> },
    { title: 'Taxable', dataIndex: 'taxable', key: 'taxable', render: val => formatCurrency(val) },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: val => <span style={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(val)}</span> },
    { title: '', key: 'action', width: 40, render: (_, record) => <Button danger type="text" size="small" icon={<DeleteOutlined />} onClick={() => handleRemoveItem(record.id)} /> }
  ];

  const historyColumns = [
    { title: 'Bill No', dataIndex: 'billNo', key: 'billNo' },
    { title: 'Date', dataIndex: 'billDate', key: 'billDate', render: val => formatDate(val) },
    { title: 'Vendor', dataIndex: 'vendorName', key: 'vendorName' },
    { title: 'Total', dataIndex: 'totalAmount', key: 'totalAmount', render: val => <span style={{ fontWeight: 600 }}>{formatCurrency(val)}</span> },
    {
      title: 'Action', key: 'actions', width: 80,
      render: (_, record) => (
        <Space size="small">
          {canEdit && <Button type="text" size="small" icon={<EditOutlined style={{ color: '#1890ff' }}/>} onClick={() => handleEditBill(record)} />}
          {canDelete && <ConfirmDelete itemName={record.billNo} onConfirm={() => deleteMutation.mutate(record.rpsid)} />}
        </Space>
      )
    }
  ];

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <LightThemeStyles />
      <div className="light-purchase-page" style={{ padding: '8px 16px' }} ref={containerRef}>

        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase' }}>
                {editingRpsid ? 'Edit Purchase Entry' : 'Raw Purchase Entry'}
              </span>
            </div>
          }
          extra={editingRpsid && <Button onClick={handleResetForm} danger size="small">Cancel Edit</Button>}
          styles={{ header: { padding: '8px 16px', minHeight: 40 }, body: { padding: '12px 16px' } }}
        >
          
          {/* COMPACT SECTION 1: BILL HEADER */}
          <div className="compact-section">
            <div className="section-title">1. Bill Header</div>
            <Form form={headerForm} layout="vertical" initialValues={{ gstPercent: 18, billDate: dayjs() }}>
              <Row gutter={12} align="bottom">
                <Col xs={24} md={9}className="vendor-search-container">
                  <Form.Item label="Vendor" name="vid" rules={[{ required: true, message: 'Req' }]}>
                    <SearchableSelect
                      apiUrl={`/plants/${plantId}/vendors/search`} valueField="vid" labelField="vendorName"
                      placeholder="Search vendor..." size="small"
                      onSelect={v => {
                        headerForm.setFieldsValue({ vid: v ? v.vid : undefined }); setSelectedVendor(v);
                        if (v) { setHistoryVendorId(v.vid); setHistoryParams(prev => ({ ...prev, current: 1 })); }
                      }}
                    />
                  </Form.Item>
                  {selectedVendor && selectedVendor.gstin && (
                    <div style={{ position: 'absolute', top: '-22px', right: '8px', fontSize: '11px', color: '#64748b' }}>
                      GSTIN: <b>{selectedVendor.gstin}</b>
                    </div>
                  )}
                </Col>
                <Col xs={12} md={5}>
                  <Form.Item label="Bill No" name="billNo" rules={[{ required: true, message: 'Req' }]}>
                    <Input size="small" placeholder="Bill No" />
                  </Form.Item>
                </Col>
                 <Col xs={12} md={5}>
                 <Form.Item label="Global GST %" name="gstPercent">
                <Select size="small" options={GST_RATES} showSearch />
              </Form.Item>
                </Col>
                <Col xs={12} md={5}>
                  <Form.Item label="Date" name="billDate" rules={[{ required: true, message: 'Req' }]}>
                    <DatePicker size="small" style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
               
              </Row>
            </Form>
          </div>

          {/* COMPACT SECTION 2: ADD ITEM */}
          <div className="compact-section">
            <div className="section-title">2. Add Item <span style={{ textTransform: 'none', fontWeight: 'normal', color: '#94a3b8' }}>(Press Enter to move next)</span></div>
            <Form form={itemForm} layout="vertical">
              <Row gutter={8} align="bottom">
                
                {/* 🔴 CLASS ADDED HERE for material search container */}
                <Col xs={24} md={7} className="material-search-container">
                  <Form.Item label="Material" name="rawID" rules={[{ required: true, message: 'Req' }]}>
                    <SearchableSelect
                      ref={rawMaterialRef}
                      size="small" apiUrl={`/plants/${plantId}/raw-materials`} valueField="rawID" labelField="rawName" placeholder="Search..."
                      onSelect={(v) => {
                        itemForm.setFieldsValue({ rawID: v ? v.rawID : undefined }); setSelectedRaw(v);
                        if (v) { 
                            itemForm.setFieldsValue({ hsnCode: v.hsnCode || '', unitDisplay: v.unitDisplayName || '' });
                            // Jump to Qty field automatically
                            setTimeout(() => { document.getElementById('qty')?.focus(); }, 100);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                
                <Col xs={12} md={3}>
                  <Form.Item label="HSN" name="hsnCode"><Input size="small" placeholder="HSN" /></Form.Item>
                </Col>
                
                {/* 🔴 ID ADDED HERE for Qty Focus */}
                <Col xs={12} md={3}>
                  <Form.Item label="Qty" name="qty" rules={[{ required: true, message: 'Req' }]}><Input id="qty" size="small" type="number" step="any" min="0" placeholder="0" /></Form.Item>
                </Col>
                
                <Col xs={12} md={3}>
                  <Form.Item label="Unit" name="unitDisplay"><Input size="small" disabled placeholder="Auto" style={{ background: '#f1f5f9' }} /></Form.Item>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Item label="Rate (₹)" name="rate" rules={[{ required: true, message: 'Req' }]}><Input size="small" type="number" step="any" min="0" placeholder="0.00" /></Form.Item>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Item label="Discount" name="discount"><Input size="small" type="number" step="any" min="0" placeholder="0.00" /></Form.Item>
                </Col>
                <Col xs={24} md={2}>
                  
                  {/* 🔴 CLASS btn-add-item ADDED HERE */}
                  <Button type="primary" size="small" className="btn-add-item tab-stop" icon={<PlusOutlined />} onClick={handleAddLineItem} block style={{ background: '#0f172a' }}>
                    Add
                  </Button>
                
                </Col>
              </Row>
            </Form>
          </div>

          {/* COMPACT SECTION 3: TABLE & TOTALS */}
          <div className="section-title" style={{ marginTop: 16 }}>3. Bill Summary</div>
          <div style={S.tableWrap}>
            <Table 
              columns={lineItemColumns} dataSource={lineItems} pagination={false} rowKey="id" size="small" scroll={{ x: 600, y: 300 }}
              locale={{ emptyText: "No items added." }}
            />
          </div>

          <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: 4, border: '1px solid #e2e8f0' }}>
            <Row align="middle" justify="space-between">
              <Col xs={24} md={16}>
                <Space size="large" wrap>
                  <Statistic title="Taxable" value={totals.taxable} precision={2} prefix="₹" valueStyle={{ fontSize: 16 }} />
                  <Statistic title="GST" value={totals.gst} precision={2} prefix="₹" valueStyle={{ fontSize: 16, color: '#64748b' }} />
                  <Statistic title="Grand Total" value={totals.grand} precision={2} prefix="₹" valueStyle={{ fontSize: 20, fontWeight: 700, color: '#059669' }} />
                </Space>
              </Col>
              <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                {canAdd && (
                  
                  /* 🔴 CLASS btn-save-bill ADDED HERE */
                  <Button type="primary" size="middle" className="btn-save-bill tab-stop" icon={<ShoppingCartOutlined />} onClick={handleSaveBill} loading={createMutation.isPending || updateMutation.isPending} disabled={lineItems.length === 0} style={{ background: 'var(--accent)', color: '#000', fontWeight: 600 }}>
                    {editingRpsid ? 'Update Bill' : 'Save Bill'}
                  </Button>
                
                )}
              </Col>
            </Row>
          </div>

          <Divider style={{ margin: '20px 0', borderColor: '#e2e8f0' }} />

          {/* HISTORY SECTION */}
          <div className="section-title"><FileTextOutlined /> Recent Vendor Bills</div>
          {historyVendorId ? (
            <>
              <div style={{ marginBottom: 8, width: 220 }}>
                <DatePicker.RangePicker size="small" style={{ width: '100%' }} onChange={(dates) => setHistoryParams(prev => ({ ...prev, current: 1, from: dates ? dates[0] : null, to: dates ? dates[1] : null }))} />
              </div>
              <div style={S.tableWrap}>
                <DataTable 
                  columns={historyColumns} data={historyData?.items || []} loading={isHistoryLoading} rowKey="rpsid"
                  pagination={{ current: historyParams.current, pageSize: historyParams.pageSize, total: historyData?.total || 0, size: 'small' }}
                  onChange={(pagination) => setHistoryParams(prev => ({ ...prev, current: pagination.current, pageSize: pagination.pageSize }))}
                  expandable={{ expandedRowRender: (record) => <ExpandedBillDetails plantId={plantId} rpsid={record.rpsid} /> }}
                />
              </div>
            </>
          ) : (
            <div style={S.emptyState}>
              <div style={S.emptyText}>Select a vendor at the top to view history</div>
            </div>
          )}

        </Card>
      </div>
    </ConfigProvider>
  );
};

export default RawPurchasePage;